import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import styles from './styles.scss';

const propTypes = {
  canDrop: PropTypes.bool,
  children: PropTypes.node.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  draggedNode: PropTypes.shape({}),
  isOver: PropTypes.bool.isRequired,
  listIndex: PropTypes.number.isRequired,
  lowerSiblingCounts: PropTypes.arrayOf(PropTypes.number).isRequired,
  style: PropTypes.shape({}),
  swapDepth: PropTypes.number,
  swapFrom: PropTypes.number,
  swapLength: PropTypes.number,
  treeIndex: PropTypes.number,
};

const defaultProps = {
  canDrop: false,
  draggedNode: undefined,
  style: undefined,
  swapDepth: undefined,
  swapFrom: undefined,
  swapLength: undefined,
  treeIndex: undefined,
};

// See https://github.com/frontend-collective/react-sortable-tree#themes for
// more information on the props available to this component.
const NavigationTreeNode = ({
  canDrop,
  children,
  connectDropTarget,
  draggedNode,
  isOver,
  listIndex,
  lowerSiblingCounts,
  style,
  swapFrom,
  swapLength,
  swapDepth,
  treeIndex,
}) => {
  const scaffoldBlockCount = lowerSiblingCounts.length;
  const scaffoldBlocks = lowerSiblingCounts.map((lowerSiblingCount, i) => {
    const firstLevel = i === 0;
    const moreNodesBelow = lowerSiblingCount > 0;
    const lastScaffoldBlock = i === scaffoldBlockCount - 1;

    const scaffoldBlockClasses = cx(
      styles.scaffoldBlock,
      !firstLevel && moreNodesBelow && !lastScaffoldBlock && styles.scaffoldBlockVertical,
      !firstLevel && moreNodesBelow && lastScaffoldBlock && styles.scaffoldBlockVerticalLeft,
      !firstLevel && !moreNodesBelow && lastScaffoldBlock && styles.scaffoldBlockBottomLeft,
    );

    const scaffoldBlock = (
      <div
        className={scaffoldBlockClasses}
        key={`scaffold-block-${i}`} // eslint-disable-line react/no-array-index-key
      />
    );

    if (treeIndex === listIndex || i !== swapDepth) {
      return scaffoldBlock;
    }

    const topLeft = treeIndex === swapFrom;
    const bottomLeft = listIndex === swapFrom + swapLength - 1;

    const lineBlockClasses = cx(
      styles.lineBlock,
      topLeft && styles.lineBlockTopLeft,
      !topLeft && !bottomLeft && styles.lineBlockVertical,
      bottomLeft && styles.lineBlockBottomLeft,
    );

    const lineBlockStyle = {
      left: `${2 * i}rem`, // $scaffold-block-width in styles.scss
    };

    return (
      <React.Fragment>
        {scaffoldBlock}
        <div
          className={lineBlockClasses}
          key={`line-block-${i}`} // eslint-disable-line react/no-array-index-key
          style={lineBlockStyle}
        />
      </React.Fragment>
    );
  });

  return connectDropTarget(
    <div className={styles.target}>
      {scaffoldBlocks}
      <div
        className={styles.node}
        style={style}
      >
        {React.Children.map(children, child => (
          React.cloneElement(child, {
            canDrop,
            draggedNode,
            isOver,
            listIndex,
            lowerSiblingCounts,
            swapDepth,
            swapFrom,
            swapLength,
          })
        ))}
      </div>
    </div>,
  );
};

NavigationTreeNode.propTypes = propTypes;
NavigationTreeNode.defaultProps = defaultProps;

export default NavigationTreeNode;
